import { useEffect, useMemo, useRef, useState } from "react";
import { LocateFixed, RefreshCw, Search, Users, Wifi } from "lucide-react";
import { api } from "../api";
import { ErrorState, LoadingState } from "../components/design-system";
import { useResource } from "../hooks/use-resource";
import type { MapLocatorPerson } from "../types";

type LngLat = { lat: number; lng: number };
type TencentMap = {
  easeTo?: (options: {
    center: LngLat;
    zoom: number;
    duration: number;
  }) => void;
  setCenter: (center: LngLat) => void;
  setZoom: (zoom: number) => void;
  destroy: () => void;
};
type MultiMarker = {
  setGeometries: (geometries: MarkerGeometry[]) => void;
  on: (name: string, callback: (event: MarkerEvent) => void) => void;
  setMap: (map: TencentMap | null) => void;
};
type InfoWindow = {
  setPosition: (position: LngLat) => void;
  setContent: (content: string) => void;
  open: () => void;
  close: () => void;
  setMap: (map: TencentMap | null) => void;
};
type MarkerGeometry = {
  id: string;
  styleId: "online" | "offline";
  position: LngLat;
};
type MarkerEvent = { geometry?: { id?: string } };
type TencentMaps = {
  Map: new (
    container: HTMLElement,
    options: {
      center: LngLat;
      zoom: number;
      viewMode: "2D";
      showControl: boolean;
    },
  ) => TencentMap;
  LatLng: new (lat: number, lng: number) => LngLat;
  MarkerStyle: new (options: Record<string, unknown>) => unknown;
  MultiMarker: new (options: Record<string, unknown>) => MultiMarker;
  InfoWindow: new (options: Record<string, unknown>) => InfoWindow;
};

declare global {
  interface Window {
    TMap?: TencentMaps;
    easySwuTencentMap?: Promise<TencentMaps>;
  }
}

function loadTencentMap(key: string): Promise<TencentMaps> {
  if (window.TMap) return Promise.resolve(window.TMap);
  if (window.easySwuTencentMap) return window.easySwuTencentMap;
  window.easySwuTencentMap = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://map.qq.com/api/gljs?v=1.exp&key=${encodeURIComponent(key)}`;
    script.async = true;
    const fail = () => {
      script.remove();
      window.easySwuTencentMap = undefined;
      reject(new Error("地图组件加载失败"));
    };
    script.onload = () => (window.TMap ? resolve(window.TMap) : fail());
    script.onerror = fail;
    document.head.appendChild(script);
  });
  return window.easySwuTencentMap;
}

function markerIcon(color: string, online: boolean): string {
  const svg = online
    ? `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="7" fill="${color}" stroke="#fffdf9" stroke-width="3"/><circle cx="12" cy="12" r="10.25" fill="none" stroke="${color}" stroke-opacity=".28" stroke-width="1.5"/></svg>`
    : `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14"><circle cx="7" cy="7" r="6" fill="${color}" stroke="#ffffff" stroke-width="1.5"/></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

export function toTencentLatLng(
  TMap: Pick<TencentMaps, "LatLng">,
  latitude: number,
  longitude: number,
): LngLat {
  return new TMap.LatLng(latitude, longitude);
}

export function formatMapMinute(value: string | null): string {
  if (!value) return "未知";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "未知";
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

export function findMapPeople(
  people: MapLocatorPerson[],
  query: string,
): MapLocatorPerson[] {
  const needle = query.normalize("NFKC").trim().toLocaleLowerCase();
  if (!needle) return [];
  return people
    .filter((person) =>
      [person.displayName, person.account, person.studentNumber || ""].some(
        (value) => value.normalize("NFKC").toLocaleLowerCase().includes(needle),
      ),
    )
    .sort(
      (a, b) =>
        Number(
          b.account === needle || b.displayName.toLocaleLowerCase() === needle,
        ) -
          Number(
            a.account === needle ||
              a.displayName.toLocaleLowerCase() === needle,
          ) ||
        Number(b.online) - Number(a.online) ||
        a.displayName.localeCompare(b.displayName, "zh-CN"),
    );
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        char
      ]!,
  );
}

function popup(person: MapLocatorPerson): string {
  return `<div class="locator-popup"><strong>${escapeHtml(person.displayName)}</strong><span>${escapeHtml(person.studentNumber || "—")}</span><small>最后更新：${escapeHtml(formatMapMinute(person.updatedAt))}</small></div>`;
}

export function MapLocatorPage() {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<TencentMap | null>(null);
  const markers = useRef<MultiMarker[]>([]);
  const info = useRef<InfoWindow | null>(null);
  const peopleRef = useRef<MapLocatorPerson[]>([]);
  const selectedId = useRef<string | null>(null);
  const [mapError, setMapError] = useState<Error | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const config = useResource(() => api.mapLocatorConfig());
  const people = useResource(() => api.mapLocatorPeople(), [], 10000);
  peopleRef.current = people.data?.people || [];
  const results = useMemo(
    () => findMapPeople(peopleRef.current, query).slice(0, 8),
    [people.data, query],
  );

  const showPerson = (person: MapLocatorPerson, move = false) => {
    if (!map.current || !info.current || !window.TMap) return;
    const center = toTencentLatLng(
      window.TMap,
      person.latitude,
      person.longitude,
    );
    info.current.setPosition(center);
    info.current.setContent(popup(person));
    info.current.open();
    if (move) {
      selectedId.current = person.id;
      if (map.current.easeTo)
        map.current.easeTo({ center, zoom: 18, duration: 760 });
      else {
        map.current.setCenter(center);
        map.current.setZoom(18);
      }
    }
  };

  useEffect(() => {
    if (!config.data || !container.current || map.current) return;
    let active = true;
    void loadTencentMap(config.data.key)
      .then((TMap) => {
        if (!active || !container.current) return;
        const center = toTencentLatLng(
          TMap,
          config.data!.center.latitude,
          config.data!.center.longitude,
        );
        map.current = new TMap.Map(container.current, {
          center,
          zoom: 16,
          viewMode: "2D",
          showControl: false,
        });
        const styles = {
          online: new TMap.MarkerStyle({
            width: 24,
            height: 24,
            anchor: { x: 12, y: 12 },
            src: markerIcon("#2f9b68", true),
          }),
          offline: new TMap.MarkerStyle({
            width: 14,
            height: 14,
            anchor: { x: 7, y: 7 },
            src: markerIcon("#c95145", false),
          }),
        };
        // Separate layers keep online users above offline users across refreshes.
        markers.current = ["offline", "online"].map(
          (status, index) =>
            new TMap.MultiMarker({
              id: `locator-${status}`,
              map: map.current,
              zIndex: 100 + index,
              styles,
              geometries: [],
            }),
        );
        info.current = new TMap.InfoWindow({
          map: map.current,
          position: center,
          content: "",
          offset: { x: 0, y: -16 },
        });
        info.current.close();
        const open = (event: MarkerEvent) => {
          const person = peopleRef.current.find(
            (item) => item.id === event.geometry?.id,
          );
          if (person) showPerson(person);
        };
        for (const marker of markers.current) {
          marker.on("hover", (event) => {
            if (event?.geometry) open(event);
            else if (!selectedId.current) info.current?.close();
          });
          marker.on("click", open);
        }
        setMapError(null);
        setMapReady(true);
      })
      .catch(
        (error: unknown) =>
          active &&
          setMapError(
            error instanceof Error ? error : new Error("地图加载失败"),
          ),
      );
    return () => {
      active = false;
      for (const marker of markers.current) marker.setMap(null);
      info.current?.setMap(null);
      map.current?.destroy();
      markers.current = [];
      setMapReady(false);
      info.current = null;
      map.current = null;
    };
  }, [config.data]);

  useEffect(() => {
    if (!mapReady || !window.TMap || !people.data) return;
    const currentPeople = people.data.people;
    markers.current.forEach((marker, index) =>
      marker.setGeometries(
        currentPeople
          .filter((person) => person.online === (index === 1))
          .map((person) => ({
            id: person.id,
            styleId: person.online ? "online" : "offline",
            position: toTencentLatLng(
              window.TMap!,
              person.latitude,
              person.longitude,
            ),
          })),
      ),
    );
    const selected = people.data.people.find(
      (person) => person.id === selectedId.current,
    );
    if (selected) showPerson(selected);
    else if (selectedId.current) {
      selectedId.current = null;
      info.current?.close();
    }
  }, [people.data, mapReady]);

  const choose = (person: MapLocatorPerson) => {
    setQuery(`${person.displayName} · ${person.studentNumber || "—"}`);
    setFocused(false);
    showPerson(person, true);
  };
  const locate = () => {
    const matches = findMapPeople(peopleRef.current, query);
    if (matches[0]) choose(matches[0]);
  };
  const online =
    people.data?.people.filter((person) => person.online).length || 0;

  return (
    <section className="locator-page rise-in">
      <div className="locator-map-shell">
        <div
          ref={container}
          className="locator-map"
          aria-label="用户位置地图"
        />
        <div className="locator-search-wrap">
          <form
            className="locator-search"
            onSubmit={(event) => {
              event.preventDefault();
              locate();
            }}
          >
            <Search size={16} />
            <input
              value={query}
              onChange={(event) => {
                selectedId.current = null;
                setQuery(event.target.value);
              }}
              onFocus={() => setFocused(true)}
              onBlur={() => window.setTimeout(() => setFocused(false), 120)}
              placeholder="搜索姓名或学号"
              aria-label="搜索姓名或学号"
            />
            <button
              type="submit"
              aria-label="定位到用户"
              disabled={!results.length}
            >
              <LocateFixed size={16} />
            </button>
          </form>
          {focused && query.trim() && (
            <div className="locator-results">
              {results.length ? (
                results.map((person) => (
                  <button
                    key={person.id}
                    type="button"
                    onMouseDown={() => choose(person)}
                  >
                    <span
                      className={`locator-dot ${person.online ? "is-online" : ""}`}
                    />
                    <strong>{person.displayName}</strong>
                    <small>{person.studentNumber || "—"}</small>
                  </button>
                ))
              ) : (
                <p>未找到匹配用户</p>
              )}
            </div>
          )}
        </div>
        <div className="locator-status" aria-live="polite">
          <span>
            <Users size={13} />
            {people.data?.people.length || 0} 人
          </span>
          <span>
            <Wifi size={13} />
            {online} 在线
          </span>
          {people.refreshing && (
            <RefreshCw size={13} className="animate-spin" />
          )}
        </div>
        {(config.loading || people.loading) && (
          <div className="locator-state">
            <LoadingState rows={4} />
          </div>
        )}
        {(config.error || people.error || mapError) && (
          <div className="locator-state">
            <ErrorState
              error={
                config.error ||
                people.error ||
                mapError ||
                new Error("地图加载失败")
              }
              onRetry={() =>
                void Promise.all([config.refresh(), people.refresh()])
              }
            />
          </div>
        )}
      </div>
    </section>
  );
}
